// ─────────────────────────────────────────────────────────────────────────────
// App Badge Helper (FEAT-022)
//
// Drei Mechanismen zur Anzeige von ungelesenen Notifications:
//   1. Tab-Title: "(3) Baumwächter" — funktioniert überall
//   2. Badging API: navigator.setAppBadge(n) — Desktop Chrome/Edge,
//      Android Chrome (PWA installiert), iOS 16.4+ (PWA installiert)
//   3. Favicon-Overlay: roter Punkt mit Zahl auf dem Favicon — Fallback
//      für Browser ohne Badging API
//
// Wird vom NotificationsBell.tsx aufgerufen, sobald der unreadCount geupdated wird.
// ─────────────────────────────────────────────────────────────────────────────

const BASE_TITLE = 'Baumwächter';

// Zwischenspeicher des original Favicons (für "zurücksetzen" wenn count = 0)
let originalFaviconHref: string | null = null;
let badgedFaviconCache: string | null = null;
let lastBadgedCount = -1;

// ── 1. Tab-Title ────────────────────────────────────────────────────────────
function updateTabTitle(count: number) {
  if (count > 0) {
    document.title = `(${count > 99 ? '99+' : count}) ${BASE_TITLE}`;
  } else {
    document.title = BASE_TITLE;
  }
}

// ── 2. Badging API (für installierte PWAs) ──────────────────────────────────
function updateAppBadge(count: number) {
  const nav = navigator as any;
  if (typeof nav.setAppBadge !== 'function') return;
  if (count > 0) {
    nav.setAppBadge(count).catch(() => {/* still */});
  } else if (typeof nav.clearAppBadge === 'function') {
    nav.clearAppBadge().catch(() => {/* still */});
  }
}

// ── 3. Favicon-Overlay als Fallback ─────────────────────────────────────────
//
// Zeichnet einen roten Kreis mit der Zahl in die rechte obere Ecke des Favicons.
// Cached das Ergebnis pro Count damit nicht jedesmal neu gerendert wird.

function getFaviconLink(): HTMLLinkElement | null {
  // Bevorzugt rel="icon"; fallback rel="shortcut icon"
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) link = document.querySelector<HTMLLinkElement>("link[rel='shortcut icon']");
  if (!link) {
    // Falls keiner da ist, einen anlegen
    link = document.createElement('link');
    link.rel = 'icon';
    link.href = '/favicon.ico';
    document.head.appendChild(link);
  }
  return link;
}

async function drawFaviconBadge(baseHref: string, count: number): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);

        // Original-Favicon zeichnen
        ctx.drawImage(img, 0, 0, size, size);

        // Roter Badge rechts oben
        const r = 22;
        const cx = size - r;
        const cy = r;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = '#c62828';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();

        // Zahl darin
        const label = count > 9 ? '9+' : String(count);
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${count > 9 ? 22 : 28}px -apple-system, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, cx, cy + 2);

        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = baseHref;
  });
}

async function updateFavicon(count: number) {
  const link = getFaviconLink();
  if (!link) return;
  if (originalFaviconHref === null) originalFaviconHref = link.href;

  if (count === 0) {
    // Zurücksetzen
    if (originalFaviconHref && link.href !== originalFaviconHref) {
      link.href = originalFaviconHref;
    }
    return;
  }

  // Wenn der Count gleich bleibt → nicht neu rendern
  if (count === lastBadgedCount && badgedFaviconCache) {
    if (link.href !== badgedFaviconCache) link.href = badgedFaviconCache;
    return;
  }

  // Neu rendern – nutze SVG-Favicon wenn vorhanden (für höhere Auflösung)
  const svgLink = document.querySelector<HTMLLinkElement>("link[rel='icon'][type='image/svg+xml']");
  const sourceHref = svgLink?.href || originalFaviconHref;
  if (!sourceHref) return;

  const dataUrl = await drawFaviconBadge(sourceHref, count);
  if (dataUrl) {
    badgedFaviconCache = dataUrl;
    lastBadgedCount = count;
    link.href = dataUrl;
  }
}

// ── Public API: alles auf einmal updaten ────────────────────────────────────
export function updateBadge(count: number) {
  updateTabTitle(count);
  updateAppBadge(count);
  // Favicon-Update ist async — laufen lassen ohne await
  updateFavicon(count).catch(() => {/* still */});
}
